import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";

const FEATURES = [
  {
    icon: "ri-shield-check-line",
    title: "방폭 환경 대응",
    desc: "가연성 가스나 위험 환경이 존재하는 산업 현장을 고려한 방폭 모션 시스템 구성이 가능합니다.",
  },
  {
    icon: "ri-speed-line",
    title: "정밀 속도 제어",
    desc: "Servo Drive 및 Motion Controller와 연동하여 정밀한 속도 제어를 구현합니다.",
  },
  {
    icon: "ri-ruler-2-line",
    title: "정밀 위치 제어",
    desc: "엔코더 피드백을 기반으로 반복 정밀도가 요구되는 위치 제어 시스템에 적용할 수 있습니다.",
  },
  {
    icon: "ri-repeat-2-line",
    title: "고응답 모션 제어",
    desc: "빠른 가감속과 반복 운전이 필요한 산업 자동화 설비의 모션제어에 대응합니다.",
  },
  {
    icon: "ri-settings-3-line",
    title: "컨트롤러 연동",
    desc: "Motion Controller, PLC 및 Servo Drive를 연계한 통합 제어 시스템 구성이 가능합니다.",
  },
  {
    icon: "ri-tools-line",
    title: "기술지원 및 시운전",
    desc: "모터 적용 검토부터 제어 구성, 파라미터 설정 및 현장 시운전까지 지원합니다.",
  },
];

const APPLICATIONS = [
  "위험지역 자동화 설비",
  "산업용 정밀 모션제어",
  "배터리 제조 장비",
  "Roll Press 관련 설비",
  "위치·속도 제어 시스템",
  "산업용 특수 장비",
];

export default function ExplosionProofServoMotorPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Explosion-Proof Servo Motor"
        title="방폭서보모터"
        subtitle="위험 환경의 정밀 모션제어를 위한 방폭 Servo Motor 솔루션"
        crumbs={[
          { label: "HOME", to: "/" },
          { label: "방폭서보모터" },
        ]}
        imageUrl="https://readdy.ai/api/search-image?query=industrial%20explosion%20proof%20servo%20motor%20in%20modern%20factory%2C%20heavy%20duty%20electric%20servo%20motor%2C%20precision%20motion%20control%2C%20industrial%20automation%2C%20clean%20engineering%20photography%2C%20dark%20metallic%20housing%2C%20professional%20B2B%20product%20photography&width=1920&height=900&seq=mctech-explosion-proof-servo-motor-01&orientation=landscape"
      />

      {/* Intro */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-4">
            <span className="w-6 h-px bg-accent-500" />
            Explosion-Proof Servo Motor
            <span className="w-6 h-px bg-accent-500" />
          </span>

          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 mb-6">
            산업용{" "}
            <span className="text-primary-700">방폭서보모터</span>
          </h2>

          <p className="text-base md:text-lg text-foreground-800 leading-relaxed">
            엠씨테크(MC TECH)는 산업 자동화 및 정밀 모션제어 시스템에
            적용되는{" "}
            <span className="font-bold text-primary-700">
              방폭서보모터(Explosion-Proof Servo Motor)
            </span>
            와 관련 제어 시스템의 공급 및 기술지원을 제공합니다.
            방폭 환경에서도 정밀한 위치·속도 제어가 필요한 설비를 위해
            Servo Drive, Motion Controller 및 제어 프로그램을 연계한
            시스템 구성을 지원합니다.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-10 md:mb-14 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              Motion Control
              <span className="w-6 h-px bg-accent-500" />
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
              방폭서보모터 주요 특징
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((item, i) => (
              <div
                key={item.title}
                className="bg-background-50 border border-background-200 rounded-xl p-6 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i
                      className={`${item.icon} text-2xl w-6 h-6 flex items-center justify-center`}
                    />
                  </div>

                  <span className="text-xs font-heading font-bold text-secondary-500 tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  {item.title}
                </h3>

                <p className="text-sm text-foreground-700 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* System */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              Explosion-Proof Motion
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 leading-tight">
              방폭서보모터 기반{" "}
              <span className="text-primary-700">
                정밀 모션제어 시스템
              </span>
            </h2>

            <p className="mt-5 text-base text-foreground-700 leading-relaxed">
              MC TECH는 방폭서보모터와 Servo Drive, Motion Controller,
              PLC 및 센서를 연계하여 산업용 정밀 모션제어 시스템을
              구성합니다. 설비의 운전 조건과 요구 성능을 검토하여
              위치·속도 제어, 프로그램 설정 및 현장 시운전까지
              기술지원을 제공합니다.
            </p>
          </div>

          <div className="bg-background-100 border border-background-200 rounded-xl p-7">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-5">
              주요 적용 분야
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {APPLICATIONS.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-4 py-3"
                >
                  <i className="ri-check-line text-primary-600" />
                  <span className="text-sm font-medium text-foreground-900">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Engineering */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 mb-5">
            방폭서보모터 공급부터{" "}
            <span className="text-primary-700">제어 및 시운전까지</span>
          </h2>

          <p className="text-base text-foreground-700 leading-relaxed">
            방폭서보모터 적용 검토부터 Servo Drive 및 Motion Controller
            구성, 프로그램 설정, 제어 파라미터 튜닝과 현장 시운전까지
            통합 기술지원을 제공합니다.
          </p>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
}
